/**
 * 고객 등록·편집 폼. 등록 화면과 상세의 편집 시트가 같은 컴포넌트를 쓴다.
 *
 * 화면의 zod 는 "빨간 글씨" 용이고, 저장되는 값의 기준은 서버 액션의 parse 다.
 * 그래서 여기 스키마는 전부 문자열이다 — 숫자·enum·null 로 바꾸는 일을
 * 두 곳에서 하면 두 곳이 어긋난다.
 *
 * `sections` 로 정보 칸만·상담 체크만 따로 낼 수 있다. 부분 편집이 나머지 칸을
 * 지우지 않는 건 서버 액션이 FormData 에 실제로 담긴 칸만 패치하기 때문이다.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@/lib/nav";
import { ReactNode, useState } from "react";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import SaveBar from "@/components/save-bar";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@buildpy/ui/select";
import { Switch } from "@buildpy/ui/switch";
import { Textarea } from "@buildpy/ui/textarea";
import { formatNumber, toDateInput } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { createCustomer, updateCustomer } from "@/api/actions/customer";
import { ADDRESS_FIELDS, AddressGroup, EMPTY_ADDRESS } from "@buildpy/core/modules/customer/address";
import { manualSourceChoices } from "@buildpy/core/modules/customer/intake-site";
import { PasteFormValues } from "@buildpy/core/modules/customer/paste-fill";
import {
  BUDGET_BAND_LABEL,
  BUDGET_BANDS,
  CALL_TIME_LABEL,
  CALL_TIMES,
  ConsultNeed,
  CUSTOMER_SOURCE_LABEL,
  Customer,
  WORK_SCOPE_LABEL,
  WORK_SCOPES,
} from "@buildpy/core/modules/customer/types";
import AddressField from "@/modules/customer/ui/address-field";
import type { PasteOwnInfo } from "@buildpy/core/modules/customer/paste-parse";
import PasteInquiryPanel from "@/modules/customer/ui/paste-inquiry-panel";
import type { CodeOption } from "@buildpy/core/modules/org/codes";
import { TradeGroup } from "@buildpy/core/modules/template/trades";

/** 라디오·셀렉트의 "고르지 않음". Radix Select 는 빈 문자열 값을 허용하지 않는다. */
const NONE = "__none__";

const NO_CHOICE_LABEL = "선택 안 함";

const DATE = /^(\d{4}-\d{2}-\d{2})?$/;

const consultRowSchema = z.object({
  // 회사가 추가한 공종 코드가 들어온다. 판정은 서버가 한다.
  trade: z.string().trim().min(1),
  checked: z.boolean(),
  detail: z.string().trim().max(500, "500자 이내로 적어 주세요"),
  note: z.string().trim().max(500, "500자 이내로 적어 주세요"),
});

const formSchema = z.object({
  name: z.string().trim().min(1, "이름을 입력하세요").max(50),
  phone: z.string().trim().min(1, "휴대폰 번호를 입력하세요").max(30),
  residence: z.string(),
  area_band: z.string(),
  space_state: z.string(),
  need_extension: z.boolean(),
  move_in_hope: z.string().regex(DATE, "YYYY-MM-DD 형식이어야 합니다"),
  work_start_hope: z.string().regex(DATE, "YYYY-MM-DD 형식이어야 합니다"),
  survey_date: z.string().regex(DATE, "YYYY-MM-DD 형식이어야 합니다"),
  budget: z.string().regex(/^[\d,\s]*$/, "숫자만 입력하세요"),
  work_scope: z.string(),
  budget_band: z.string(),
  call_time: z.string(),
  source: z.string(),
  assignee_id: z.string(),
  memo: z.string().trim().max(2000, "2000자 이내로 적어 주세요"),
  consult: z.array(consultRowSchema),
});

type FormValues = z.infer<typeof formSchema>;

export type CustomerFormSection = "info" | "consult";

export interface MemberOption {
  id: string;
  name: string;
}

/**
 * 거주형태·평형대·공간 상태의 선택지. **회사 코드값**이라 서버가 만들어 넘긴다 —
 * 화면이 `modules/org/codes` 를 값으로 부르면 인메모리 시드가 브라우저로 딸려 온다.
 * 지금 저장된 값이 사용 안 함이어도 그 줄은 남아 있다(`toOptions`).
 */
export interface CustomerCodeOptions {
  residence: CodeOption[];
  area_band: CodeOption[];
  space_state: CodeOption[];
}

/** 상담 체크에 깔 공종. 「빌드파이 표준 + 이 회사」 를 서버가 합쳐 준다. */
export interface ConsultTradeOption {
  code: string;
  name: string;
  group: TradeGroup;
}

interface Props {
  members: MemberOption[];
  codes: CustomerCodeOptions;
  trades: ConsultTradeOption[];
  /** 없으면 등록, 있으면 그 고객 수정. */
  customer?: Customer | null;
  sections?: CustomerFormSection[];
  submitLabel?: string;
  /** 시트 안에서 쓸 때 저장 후 닫기. */
  onDone?: () => void;
  onCancel?: () => void;
  // 붙여넣기에서 뺄 우리 회사 번호·주소(등록 화면만).
  pasteOwn?: PasteOwnInfo;
}

/** 공종을 늘 같은 순서로 깔고, 이미 체크된 것만 채워 넣는다. */
const toConsultRows = (
  saved: ConsultNeed[],
  trades: readonly ConsultTradeOption[]
): FormValues["consult"] =>
  trades.map((trade) => {
    const found = saved.find((row) => row.trade === trade.code);
    return {
      trade: trade.code,
      checked: Boolean(found),
      detail: found?.detail ?? "",
      note: found?.note ?? "",
    };
  });

const pickAddress = (customer: Customer): Omit<AddressGroup, "site_address"> =>
  Object.fromEntries(ADDRESS_FIELDS.map((key) => [key, customer[key] ?? null])) as Omit<AddressGroup, "site_address">;

const toDefaults = (
  trades: readonly ConsultTradeOption[],
  customer?: Customer | null
): FormValues => ({
  name: customer?.name ?? "",
  phone: customer?.phone ?? "",
  residence: customer?.residence ?? "",
  area_band: customer?.area_band ?? "",
  space_state: customer?.space_state ?? "",
  need_extension: customer?.need_extension ?? false,
  move_in_hope: toDateInput(customer?.move_in_hope),
  work_start_hope: toDateInput(customer?.work_start_hope),
  survey_date: toDateInput(customer?.survey_date),
  budget: typeof customer?.budget === "number" ? formatNumber(customer.budget) : "",
  work_scope: customer?.work_scope ?? "",
  budget_band: customer?.budget_band ?? "",
  call_time: customer?.call_time ?? "",
  source: customer?.source ?? "",
  assignee_id: customer?.assignee_id ?? "",
  memo: customer?.memo ?? "",
  consult: toConsultRows(customer?.consult ?? [], trades),
});

const CustomerForm = ({
  members,
  codes,
  trades,
  customer,
  sections = ["info", "consult"],
  submitLabel,
  onDone,
  onCancel,
  pasteOwn,
}: Props) => {
  const router = useRouter();
  const [failure, setFailure] = useState<string | null>(null);
  // 처음 값을 고정해 입력칸 defaultValue 로도 싣는다 — register 는 값을 ref 로 나중에 넣어 서버 HTML 이 빈칸이 된다.
  const [defaults] = useState(() => toDefaults(trades, customer));
  // 주소는 검색 결과(코드)까지 한 묶음이라 RHF 밖에서 든다 — 보낼 때 FormData 에 같이 싣는다.
  const [address, setAddress] = useState<AddressGroup>(() => ({
    ...EMPTY_ADDRESS,
    ...(customer ? pickAddress(customer) : {}),
    site_address: customer?.site_address ?? "",
  }));

  const {
    control,
    formState: { errors, isDirty, isSubmitted, isSubmitting },
    getValues,
    handleSubmit,
    register,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults,
  });

  const consultRows = useWatch({ control, name: "consult" });
  // 붙여넣기 패널의 휴대폰 후보 칩이 지금 칸 값과 견준다.
  const phoneNow = useWatch({ control, name: "phone" });

  const hasInfo = sections.includes("info");
  const hasConsult = sections.includes("consult");

  // 붙여넣기로 주소를 채우면 AddressField 가 처음 값만 읽으므로 key 를 바꿔 다시 띄운다(손 입력 글자로만 · 코드는 비움).
  const [addressKey, setAddressKey] = useState(0);
  const readPaste = (): PasteFormValues => {
    const [name, phone, area_band, memo] = getValues(["name", "phone", "area_band", "memo"]);
    return { name, phone, area_band, memo, site_address: address.site_address };
  };
  const applyPaste = ({ site_address, ...rest }: Partial<PasteFormValues>) => {
    for (const [key, value] of Object.entries(rest) as [keyof typeof rest, string][]) setValue(key, value, { shouldDirty: true, shouldValidate: isSubmitted });
    if (site_address === undefined) return;
    setAddress({ ...EMPTY_ADDRESS, site_address });
    setAddressKey((k) => k + 1);
  };

  const submit = handleSubmit(async (values) => {
    setFailure(null);

    const form = new FormData();

    if (hasInfo) {
      form.set("name", values.name);
      form.set("phone", values.phone);
      form.set("site_address", address.site_address);
      for (const key of ADDRESS_FIELDS) form.set(key, address[key] ?? "");
      form.set("residence", values.residence);
      form.set("area_band", values.area_band);
      form.set("space_state", values.space_state);
      form.set("need_extension", values.need_extension ? "true" : "false");
      form.set("move_in_hope", values.move_in_hope);
      form.set("work_start_hope", values.work_start_hope);
      form.set("survey_date", values.survey_date);
      form.set("budget", values.budget);
      form.set("work_scope", values.work_scope);
      form.set("budget_band", values.budget_band);
      form.set("call_time", values.call_time);
      form.set("source", values.source);
      form.set("assignee_id", values.assignee_id);
      form.set("memo", values.memo);
    }

    if (hasConsult) {
      form.set(
        "consult",
        JSON.stringify(
          values.consult
            .filter((row) => row.checked)
            .map(({ trade, detail, note }) => ({ trade, detail, note }))
        )
      );
    }

    try {
      if (customer) {
        const result = await updateCustomer(customer.id, form);
        if (!result.ok) {
          setFailure(result.message);
          return;
        }
        toast.success(hasInfo ? "고객 정보를 저장했습니다" : "상담 체크를 저장했습니다");
        router.refresh();
        onDone?.();
        return;
      }
      // 성공하면 액션이 상세로 redirect 한다(아래 settleRedirect 가 조용히 끝낸다).
      const result = await createCustomer(form);
      if (result?.ok === false) setFailure(result.message);
    } catch (error) {
      if (settleRedirect(error)) return;
      setFailure("저장하지 못했습니다. 입력을 확인하고 다시 시도하세요.");
    }
  });

  return (
    <form className="flex flex-col gap-y-6" onSubmit={submit}>
      {!customer && hasInfo && <PasteInquiryPanel apply={applyPaste} areaBands={codes.area_band} own={pasteOwn} phone={phoneNow} read={readPaste} />}
      {hasInfo && (
        <Section title="고객 정보">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              {...register("name")}
              defaultValue={defaults.name}
              errorMessage={errors.name?.message}
              id="name"
              label="이름 *"
              placeholder="예: 홍길동"
            />
            <Input
              {...register("phone")}
              defaultValue={defaults.phone}
              errorMessage={errors.phone?.message}
              id="phone"
              label="휴대폰 *"
              placeholder="예: 010-1234-5678"
            />

            <div className="md:col-span-2">
              <AddressField
                key={addressKey}
                initial={address}
                label="현장 주소"
                placeholder="예: 서울 ○○구 ○○아파트 101동 1001호"
                onChange={setAddress}
              />
            </div>

            <SelectField
              control={control}
              label="거주형태"
              name="residence"
              options={codes.residence}
            />
            <SelectField
              control={control}
              label="평형대"
              name="area_band"
              options={codes.area_band}
            />
            <SelectField
              control={control}
              label="현재 공간 상태"
              name="space_state"
              options={codes.space_state}
            />

            <Field label="확장 필요">
              <Controller
                control={control}
                name="need_extension"
                render={({ field }) => (
                  <div className="flex min-h-10 items-center gap-x-2">
                    <Switch
                      aria-describedby="need_extension-label"
                      aria-label="확장 필요"
                      checked={field.value}
                      id="need_extension"
                      onCheckedChange={field.onChange}
                    />
                    <span className="text-sm text-muted-foreground" id="need_extension-label">
                      {field.value ? "확장 필요" : "확장 없음"}
                    </span>
                  </div>
                )}
              />
            </Field>

            <Input
              {...register("move_in_hope")}
              defaultValue={defaults.move_in_hope}
              errorMessage={errors.move_in_hope?.message}
              id="move_in_hope"
              label="예상 입주일"
              type="date"
            />
            <Input
              {...register("work_start_hope")}
              defaultValue={defaults.work_start_hope}
              errorMessage={errors.work_start_hope?.message}
              id="work_start_hope"
              label="공사 예정일"
              type="date"
            />
            <Input
              {...register("survey_date")}
              defaultValue={defaults.survey_date}
              errorMessage={errors.survey_date?.message}
              id="survey_date"
              label="실측 가능일"
              type="date"
            />
            <Input
              {...register("budget")}
              defaultValue={defaults.budget}
              errorMessage={errors.budget?.message}
              id="budget"
              inputMode="numeric"
              label="예산 (원)"
              placeholder="예: 50,000,000"
            />
            <SelectField
              control={control}
              label="공사 종류"
              name="work_scope"
              options={WORK_SCOPES.map((value) => ({ value, label: WORK_SCOPE_LABEL[value] }))}
            />
            <SelectField
              control={control}
              label="예산대"
              name="budget_band"
              options={BUDGET_BANDS.map((value) => ({ value, label: BUDGET_BAND_LABEL[value] }))}
            />
            <SelectField
              control={control}
              label="통화 가능 시간"
              name="call_time"
              options={CALL_TIMES.map((value) => ({ value, label: CALL_TIME_LABEL[value] }))}
            />

            <SelectField
              control={control}
              label="유입 경로"
              name="source"
              options={manualSourceChoices(customer?.source ?? null).map((value) => ({
                value,
                label: CUSTOMER_SOURCE_LABEL[value],
              }))}
            />
            <SelectField
              control={control}
              label="담당자"
              name="assignee_id"
              options={members.map((member) => ({
                value: member.id,
                label: member.name,
              }))}
            />

            <div className="flex flex-col gap-y-1.5 md:col-span-2">
              <Label htmlFor="memo">메모</Label>
              <Textarea
                {...register("memo")}
                defaultValue={defaults.memo}
                id="memo"
                placeholder="예: 저녁 통화 선호 · 욕실 누수 걱정"
              />
              {errors.memo?.message && (
                <span className="text-sm text-destructive">
                  {errors.memo.message}
                </span>
              )}
            </div>
          </div>
        </Section>
      )}

      {hasConsult && (
        <Section
          description="상담하며 필요한 공종만 체크합니다. 체크한 공종이 견적 초안의 출발점이 됩니다."
          title="공종별 상담 체크">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="consult-checks">
            {trades.map((trade, index) => (
              <div key={trade.code} className="flex min-h-9 items-center gap-x-2">
                <Controller
                  control={control}
                  name={`consult.${index}.checked`}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      id={`consult-${trade.code}`}
                      onCheckedChange={(value) => field.onChange(value === true)}
                    />
                  )}
                />
                <Label className="cursor-pointer" htmlFor={`consult-${trade.code}`} variant="option">
                  {trade.name}
                </Label>
              </div>
            ))}
          </div>
          {/* 체크한 공종만 상세내역·특이사항 칸을 편다 — 안 편 칸의 값은 폼 상태에 남는다. */}
          {trades.some((_, index) => consultRows?.[index]?.checked) ? (
            <div className="flex flex-col divide-y divide-border rounded-md border border-border" data-testid="consult-details">
              {trades.map((trade, index) =>
                consultRows?.[index]?.checked ? (
                  <div key={trade.code} className="grid grid-cols-1 items-center gap-2 p-2.5 md:grid-cols-[10rem_1fr_1fr]">
                    <span className="text-sm font-medium">{trade.name}</span>
                    <Input
                      {...register(`consult.${index}.detail`)}
                      aria-label={`${trade.name} 상세내역`}
                      defaultValue={defaults.consult[index]?.detail}
                      inputSize="sm"
                      id={`consult-${trade.code}-detail`}
                      placeholder="상세내역"
                    />
                    <Input
                      {...register(`consult.${index}.note`)}
                      aria-label={`${trade.name} 특이사항`}
                      defaultValue={defaults.consult[index]?.note}
                      inputSize="sm"
                      id={`consult-${trade.code}-note`}
                      placeholder="특이사항"
                    />
                  </div>
                ) : null
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">공종을 체크하면 상세내역·특이사항 칸이 열립니다.</p>
          )}
        </Section>
      )}

      {failure && <p className="text-sm text-destructive">{failure}</p>}

      {/* 등록 화면은 긴 폼이라 저장 줄을 바닥에 붙인다 — 편집 시트는 원래 단추 줄이다. */}
      {!customer && !onCancel ? (
        <SaveBar
          alwaysEnabled
          className="bg-card"
          dirty={isDirty || address.site_address !== ""}
          label={submitLabel ?? "고객 등록"}
          pending={isSubmitting}
        />
      ) : (
        <div className="flex items-center justify-end gap-x-2">
          {onCancel && (
            <Button size="sm" type="button" variant="outline" onClick={onCancel}>
              취소
            </Button>
          )}
          <Button disabled={isSubmitting} size="sm" type="submit">
            {submitLabel ?? (customer ? "저장" : "고객 등록")}
          </Button>
        </div>
      )}
    </form>
  );
};

export default CustomerForm;

/* ── 조각들 ────────────────────────────────────────────────── */

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-y-3">
    <div className="flex flex-col gap-y-0.5">
      <h2 className="text-sm font-semibold">{title}</h2>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </section>
);

const Field = ({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) => (
  <div className="flex w-full flex-col items-start gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);

type SelectFieldName =
  | "residence"
  | "area_band"
  | "space_state"
  | "work_scope"
  | "budget_band"
  | "call_time"
  | "source"
  | "assignee_id";

const SelectField = ({
  control,
  name,
  label,
  options,
}: {
  control: Control<FormValues>;
  name: SelectFieldName;
  label: string;
  options: { value: string; label: string }[];
}) => (
  <Field htmlFor={name} label={label}>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          value={field.value || NONE}
          onValueChange={(value) =>
            field.onChange(value === NONE ? "" : value)
          }>
          <SelectTrigger className="text-sm" id={name} size="sm">
            {/* 고른 이름을 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다. */}
            <SelectValue placeholder="선택">
              {field.value ? options.find((o) => o.value === field.value)?.label : NO_CHOICE_LABEL}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{NO_CHOICE_LABEL}</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  </Field>
);
